import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import styles from "../../../style/cssModules/table/TableComponent.module.css";
import actionIcon from "../../images/icons/Action.png";
import PermissionContextFunctions from "../../functions/PermissionContextFunctions";
import { useTranslation } from "react-i18next";
import useRTLContext from "../../../hooks/useRTLContext";

const ActionItem = ({
  currentUser = false,
  firstDropdownItem = null,
  moduleName = "",
  row,
  rowIndex,
  handleEdit,
  canUpdate,
  handleDeactivate,
  handleDelete = () => {},
  permissionName,
  isDelete = true,
  isDraft = false,
  handleView = null,
  canView = false,
  secondDropdownItem = null,
  isInvite = false,
  handleSendInvitation,
  invitationLabel = "",
}) => {
  const { grantPermission } = PermissionContextFunctions();
  const { isRTL, direction } = useRTLContext();
  const { t, i18n } = useTranslation();

  const threeDotsRef = useRef(null);
  const dropdownRef = useRef(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
  });

  const handleMenuClick = (rowIndex) => {
    if (openMenu === rowIndex) {
      setOpenMenu(null);
    } else {
      const rect = threeDotsRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
      setOpenMenu(rowIndex);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        threeDotsRef.current &&
        !threeDotsRef.current.contains(event.target)
      ) {
        setOpenMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const renderDisabledItem = (text, tooltip) => (
    <>
      <div
        className={`${styles.dropdownItem} ${styles.disabledItem}`}
        title={tooltip}
        data-tooltip={tooltip}
      >
        <span className={styles.dropdownText}>{text}</span>
      </div>
    </>
  );

  return (
    <>
      <div className={styles.actionContainer} ref={threeDotsRef}>
        <div
          className={styles.threeDots}
          onClick={() => handleMenuClick(rowIndex)}
        >
          <img src={actionIcon} alt="action icon" />
        </div>
      </div>

      {openMenu === rowIndex &&
        ReactDOM.createPortal(
          <div
            className={styles.dropdown}
            ref={dropdownRef}
            style={{
              position: "absolute",
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              zIndex: 10,
              transform: isRTL ? "none" : "translateX(-100%)", // Explicitly set transform
            }}
          >
            {isInvite && grantPermission(`${permissionName}:create`) && (
              <>
                {row?.status === "inactive" ? (
                  renderDisabledItem(
                    invitationLabel ? invitationLabel : t("Send Invitation"),
                    t("Cannot send invitation to an inactive user")
                  )
                ) : (
                  <div
                    className={styles.dropdownItem}
                    onClick={() => {
                      handleSendInvitation(row);
                      setOpenMenu(null);
                    }}
                  >
                    <span className={styles.dropdownText}>
                      {invitationLabel ? invitationLabel : t("Send Invitation")}
                    </span>
                  </div>
                )}
              </>
            )}
            {handleView &&
              (grantPermission(`${permissionName}:read`) || canView) && (
                <div
                  className={styles.dropdownItem}
                  onClick={() => {
                    handleView(row);
                    setOpenMenu(null);
                  }}
                >
                  <span className={styles.dropdownText}>
                    {firstDropdownItem
                      ? t(firstDropdownItem)
                      : t("View {{moduleName}} Details", { moduleName })}
                  </span>
                </div>
              )}
            {(grantPermission(`${permissionName}:update`) || canUpdate) &&
              row?.status !== "inactive" && (
                <div
                  className={styles.dropdownItem}
                  onClick={() => {
                    handleEdit(row);
                    setOpenMenu(null);
                  }}
                >
                  <span className={styles.dropdownText}>
                    {secondDropdownItem
                      ? t(secondDropdownItem)
                      : t("Edit {{moduleName}} Details", { moduleName })}
                  </span>
                </div>
              )}
            {grantPermission(`${permissionName}:delete`) && (
              <>
                {row?.status === "draft" ? (
                  <div
                    className={`${styles.dropdownItem} ${styles.deleteItem}`}
                    onClick={() => {
                      handleDeactivate?.(row); // Add optional chaining
                      setOpenMenu(null);
                    }}
                  >
                    <span className={styles.dropdownText}>
                      {t("Clear from Draft")}
                    </span>
                  </div>
                ) : (
                  <>
                    {currentUser ? (
                      renderDisabledItem(
                        row?.status === "active"
                          ? t("Deactivate {{moduleName}}", { moduleName })
                          : t("Reactivate {{moduleName}}", { moduleName }),
                        t("You cannot deactivate/reactivate your own account")
                      )
                    ) : handleDeactivate ? ( // Add condition to check if handler exists
                      <div
                        className={styles.dropdownItem}
                        onClick={() => {
                          handleDeactivate(row);
                          setOpenMenu(null);
                        }}
                      >
                        <span className={styles.dropdownText}>
                          {row?.status === "active"
                            ? t("Deactivate {{moduleName}}", { moduleName })
                            : t("Reactivate {{moduleName}}", { moduleName })}
                        </span>
                      </div>
                    ) : (
                      renderDisabledItem(
                        row?.status === "active"
                          ? t("Deactivate {{moduleName}}", { moduleName })
                          : t("Reactivate {{moduleName}}", { moduleName }),
                        t("System roles cannot be deactivated")
                      )
                    )}
                    {row?.status === "inactive" &&
                      isDelete &&
                      (currentUser ? (
                        renderDisabledItem(
                          t("Delete {{moduleName}}", { moduleName }),
                          t("You cannot delete your own account")
                        )
                      ) : handleDelete ? ( // Add condition for handleDelete too
                        <div
                          className={`${styles.dropdownItem} ${styles.deleteItem}`}
                          onClick={() => {
                            handleDelete(row, true);
                            setOpenMenu(null);
                          }}
                        >
                          <span className={styles.dropdownText}>
                            {t("Delete {{moduleName}}", { moduleName })}
                          </span>
                        </div>
                      ) : (
                        renderDisabledItem(
                          t("Delete {{moduleName}}", { moduleName }),
                          t("System roles cannot be deleted")
                        )
                      ))}
                  </>
                )}
              </>
            )}
          </div>,
          document.body
        )}
    </>
  );
};

export default ActionItem;
